import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface SubscriptionState {
  subscribed: boolean;
  product_id: string | null;
  subscription_end: string | null;
  is_premium: boolean;
  loading: boolean;
  error: string | null;
}

export const useSubscription = () => {
  const [state, setState] = useState<SubscriptionState>({
    subscribed: false,
    product_id: null,
    subscription_end: null,
    is_premium: false,
    loading: true,
    error: null,
  });
  const { toast } = useToast();

  const checkSubscription = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      // If no session or session error, silently set to not subscribed
      if (!session || sessionError) {
        setState({
          subscribed: false,
          product_id: null,
          subscription_end: null,
          is_premium: false,
          loading: false,
          error: null,
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      // Handle authentication errors gracefully (expired token, etc.)
      if (error) {
        // Check for authentication-related errors (401 status or auth error messages)
        const isAuthError = error.message?.includes('Auth session missing') || 
                           error.message?.includes('JWT') ||
                           error.message?.includes('Authentication error') ||
                           error.message?.includes('401');
        
        if (isAuthError) {
          // Session is invalid on server, sign out the user
          console.log('Invalid session detected, signing out...');
          await supabase.auth.signOut();
          setState({
            subscribed: false,
            product_id: null,
            subscription_end: null,
            is_premium: false,
            loading: false,
            error: null,
          });
          return;
        }
        throw error;
      }

      setState({
        subscribed: data.subscribed || false,
        product_id: data.product_id || null,
        subscription_end: data.subscription_end || null,
        is_premium: data.subscribed || false,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Error checking subscription:', error);
      // Don't show auth errors to user, just set to not subscribed
      setState({
        subscribed: false,
        product_id: null,
        subscription_end: null,
        is_premium: false,
        loading: false,
        error: null,
      });
    }
  }, []);

  const createCheckout = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: 'Authentication required',
          description: 'Please sign in to subscribe',
          variant: 'destructive',
        });
        return null;
      }

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      return data.url;
    } catch (error: any) {
      console.error('Error creating checkout:', error);
      toast({
        title: 'Checkout failed',
        description: error.message || 'Failed to create checkout session',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  const openCustomerPortal = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: 'Authentication required',
          description: 'Please sign in to manage subscription',
          variant: 'destructive',
        });
        return null;
      }

      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      return data.url;
    } catch (error: any) {
      console.error('Error opening customer portal:', error);
      toast({
        title: 'Failed to open portal',
        description: error.message || 'Failed to open customer portal',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  useEffect(() => {
    checkSubscription();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        checkSubscription();
      } else if (event === 'SIGNED_OUT') {
        setState({
          subscribed: false,
          product_id: null,
          subscription_end: null,
          is_premium: false,
          loading: false,
          error: null,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [checkSubscription]);

  return {
    ...state,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
  };
};
